import React, { useCallback, useMemo, useState } from 'react';

import { ColumnDef, OnChangeFn, Row, RowSelectionState, SortingState } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { DataDisplayTable, WithSubData } from 'ui-kit';
import { AnimationsEllipsis } from 'ui-kit/Animations/AnimatedEllipsis';

import { ApolloError, gql } from '@apollo/client';

import { AssetClass, FileState, ProductType } from '__generated__/globalTypes';
import { TapesUploadedTableFile } from './__generated__/TapesUploadedTable';
import { tapestryStageDisplay } from '../tapestry/stages';
import { isArchivable } from './isArchivable';

const TapesUploadedTableFragments = {
  loanTape: gql`
    fragment TapesUploadedTableFile on File {
      id
      name
      state
      tapestry_enabled
      tapestry_stage
      assetClass: asset_class
      productType: product_type
      createdDate: created_date
      archived_at
      original_id
      uploaded_by {
        id
        firstName: given_name
        lastName: family_name
      }
      processingReport: processing_report {
        new_count
        flagged_count
        duplicate_count
      }
      processing_percent
      total_persist_count
      persisted_count
      persisting_percent
    }
  `,
};

type InProgressFile = {
  /** uuid stamped on S3 success; matched against the server row's original_id to retire the optimistic row. */
  id?: string;
  name: string;
  lastModified: number;
  /** Carried from the upload params so the optimistic row shows the right asset class (no pop-in on swap). */
  assetClass?: AssetClass;
  productType?: ProductType;
  /** ISO timestamp captured at drop — drives the optimistic row's "Uploaded" cell and sorts it to the top. */
  startedAt?: string;
  progress?: {
    percentage: number;
  };
};

type UploadStatusCellProps = {
  percentage: number;
};

// Status pill for an optimistic (client-side) upload row. Mirrors StatusCell's
// pill exactly — same shape/size plus the green progress fill the PROCESSING /
// PERSISTING states use — so the row reads identically as it morphs
// Uploading → Reading → (server) Needs mapping with no visual seam. At 100% it
// renders the same yellow "Reading…" label the NEW/READING server states show,
// so the atomic swap to the real row is imperceptible.
const UploadStatusCell: React.FC<UploadStatusCellProps> = ({ percentage }) => {
  const reading = percentage >= 100;
  return (
    <div className="flex justify-center">
      <div className="relative inline-flex shrink-0 items-center overflow-hidden whitespace-nowrap rounded-md bg-yellow-400 px-2.5 py-1 leading-none text-gray-950">
        {!reading && (
          <div
            className="absolute left-0 top-0 h-full bg-green-400 transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        )}
        <span className="relative z-10">
          {reading ? (
            <>
              Reading <AnimationsEllipsis fontSize="18px" className="ml-1" />
            </>
          ) : (
            `Uploading ${Math.round(percentage)}%`
          )}
        </span>
      </div>
    </div>
  );
};

type StatusCellProps = {
  state: FileState;
  processingPercent?: number | null;
  persistingPercent?: number | null;
  tapestryEnabled?: boolean | null;
  tapestryStage?: string | null;
};

const StatusCell: React.FC<StatusCellProps> = ({
  state,
  processingPercent,
  persistingPercent,
  tapestryEnabled,
  tapestryStage,
}) => {
  // Tapestry tapes track their own pipeline stage; show that label instead of
  // the legacy FileState pill (which doesn't model the pipeline gates). A
  // password-protected tape never enters the pipeline, so its stage only says
  // FAILED; the FileState pill names the reason.
  if (tapestryEnabled && state !== FileState.ERROR_PASSWORD) {
    const t = tapestryStageDisplay(tapestryStage);
    return (
      <div className="flex justify-center">
        <div
          className={`relative inline-flex shrink-0 items-center overflow-hidden whitespace-nowrap rounded-md px-2.5 py-1 leading-none ${t.colorClass} ${t.textColor}`}
        >
          <span className="relative z-10">{t.label}</span>
        </div>
      </div>
    );
  }

  const { label, colorClass, textColor } = TAPE_STATE_STRINGS[state];

  // Calculate progress percentage for the fill
  let progressPercent = 0;
  if (state === FileState.PROCESSING && processingPercent != null) {
    progressPercent = processingPercent;
  } else if (state === FileState.PERSISTING && persistingPercent != null) {
    progressPercent = persistingPercent;
  }

  const showProgress =
    (state === FileState.PROCESSING || state === FileState.PERSISTING) && progressPercent > 0;

  return (
    <div className="flex justify-center">
      {/* inline-flex + shrink-0 + whitespace-nowrap: the badge sizes to its own
          label and never shrinks, so longer statuses ("Needs mapping", "Ready
          to save") aren't clipped by the badge's own overflow-hidden (which is
          kept only to clip the progress fill to the rounded corners). py-1 +
          leading-none give it height so it doesn't look compressed. */}
      <div
        className={`relative inline-flex shrink-0 items-center overflow-hidden whitespace-nowrap rounded-md px-2.5 py-1 leading-none ${colorClass} ${textColor}`}
      >
        {showProgress && (
          <div
            className="absolute left-0 top-0 h-full w-[var(--tw-progress)] bg-green-400 transition-all duration-300"
            style={{ '--tw-progress': `${progressPercent}%` } as React.CSSProperties}
          />
        )}
        <span className="relative z-10">{label}</span>
      </div>
    </div>
  );
};

const TAPE_STATE_STRINGS: Record<
  FileState,
  { label: React.ReactNode; colorClass: string; textColor?: string }
> = {
  [FileState.NEW]: {
    label: (
      <>
        Reading <AnimationsEllipsis fontSize="18px" className="ml-1" />
      </>
    ),
    colorClass: 'bg-yellow-400',
    textColor: 'text-white',
  },
  [FileState.READING_FILE_HEADERS]: {
    label: (
      <>
        Reading <AnimationsEllipsis fontSize="18px" className="ml-1" />
      </>
    ),
    colorClass: 'bg-yellow-400',
    textColor: 'text-gray-950',
  },
  [FileState.NEEDS_MAPPING]: {
    label: 'Needs mapping',
    colorClass: 'bg-blue-400',
    textColor: 'text-white',
  },
  [FileState.READY_TO_PROCESS]: {
    label: 'Processing',
    colorClass: 'bg-yellow-400',
    textColor: 'text-gray-950',
  },
  [FileState.PROCESSING]: {
    label: 'Processing',
    colorClass: 'bg-yellow-400',
    textColor: 'text-gray-950',
  },
  [FileState.PROCESSED]: {
    label: 'Ready to save',
    colorClass: 'bg-blue-400',
    textColor: 'text-white',
  },
  [FileState.READY_TO_PERSIST]: {
    label: 'Saving',
    colorClass: 'bg-yellow-400',
    textColor: 'text-gray-950',
  },
  [FileState.PERSISTING]: {
    label: 'Saving',
    colorClass: 'bg-yellow-400',
    textColor: 'text-gray-950',
  },
  [FileState.PERSISTED]: {
    label: 'Saved',
    colorClass: 'bg-green-400',
    textColor: 'text-gray-950',
  },
  [FileState.ERROR_HEADER]: {
    label: 'Error: Invalid headers',
    colorClass: 'bg-red-400',
    textColor: 'text-white',
  },
  [FileState.ERROR_PASSWORD]: {
    label: 'Error: Password protected',
    colorClass: 'bg-red-400',
    textColor: 'text-white',
  },
  [FileState.ERROR]: {
    label: 'Error',
    colorClass: 'bg-red-400',
    textColor: 'text-white',
  },
};

// States whose rows open the tape detail view on click — same set the old
// <Link>-wrapped rows used before the DataDisplayTable migration.
const NAVIGABLE_STATES: ReadonlySet<FileState> = new Set([
  FileState.NEEDS_MAPPING,
  FileState.PROCESSED,
  FileState.PROCESSING,
  FileState.PERSISTED,
  FileState.ERROR,
]);

// `__upload` marks an optimistic, client-side-only upload row (no server File
// yet). Real rows never set it. It drives the status cell and flags the row as
// non-interactive placeholder content in the other cells.
type TapeRow = WithSubData<TapesUploadedTableFile> & {
  __upload?: { percentage: number };
};

// Build a synthetic table row for an upload that hasn't been persisted
// server-side yet, so it renders as a normal (non-interactive) row in the same
// table instead of a separate floating element. state=NEW with no stage keeps it
// unarchivable and out of NAVIGABLE (disabled checkbox, no row click). createdDate=startedAt
// sorts it to the top under the default createdDate-desc sort. When the server
// row lands in the poll, UploadHistory drops this entry in the same render, so
// the real row replaces it atomically (same position, identical styling).
const buildOptimisticRow = (file: InProgressFile): TapeRow => {
  const percentage = file.progress?.percentage ?? 0;
  const key = `pending:${file.name}:${file.lastModified}`;
  return {
    id: key,
    name: file.name,
    state: FileState.NEW,
    // Optimistic rows are pre-server, so never Tapestry-routed: a false flag
    // keeps the legacy "Uploading/Reading" pill and non-navigable behavior.
    tapestry_enabled: false,
    tapestry_stage: null,
    archived_at: null,
    original_id: key,
    processing_percent: null,
    total_persist_count: null,
    persisted_count: null,
    persisting_percent: null,
    // assetClass is non-null in the schema; the drop always carries it (the
    // strip is disabled until an asset class is picked) — HOME is an unreachable
    // fallback only to satisfy the type.
    assetClass: file.assetClass ?? AssetClass.HOME,
    productType: file.productType ?? ProductType.FIXED,
    createdDate: file.startedAt ?? new Date().toISOString(),
    uploaded_by: { __typename: 'User', id: '', firstName: '', lastName: '' },
    processingReport: {
      __typename: 'ProcessingReport',
      new_count: null,
      flagged_count: null,
      duplicate_count: null,
    },
    __typename: 'File',
    __upload: { percentage },
    // Memo key must change every progress tick so the fill animates under the poll.
    last_updated_date: `upload|${percentage}`,
  };
};

// One shared tooltip for truncated filenames; cells point at it via data attrs.
const FILENAME_TOOLTIP_ID = 'tape-filename-tooltip';

const TAPE_COLUMNS: ColumnDef<TapeRow, unknown>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Filename',
    size: 220,
    enableSorting: true,
    meta: { alignment: 'left', numeric: false },
    cell: ({ row }) => (
      <div
        className="max-w-[220px] truncate"
        data-tooltip-id={FILENAME_TOOLTIP_ID}
        data-tooltip-content={row.original.name ?? ''}
      >
        {row.original.name}
      </div>
    ),
  },
  {
    id: 'assetClass',
    accessorKey: 'assetClass',
    header: 'Asset Class',
    size: 90,
    enableSorting: false,
    meta: { alignment: 'left', numeric: false },
    cell: ({ row }) =>
      `${row.original.assetClass}${
        row.original.productType === ProductType.ARM ? ' - ' + row.original.productType : ''
      }`,
  },
  {
    id: 'status',
    header: 'Status',
    size: 140,
    enableSorting: false,
    meta: { alignment: 'left', numeric: false },
    cell: ({ row }) =>
      row.original.__upload ? (
        <UploadStatusCell percentage={row.original.__upload.percentage} />
      ) : (
        <StatusCell
          state={row.original.state}
          processingPercent={row.original.processing_percent}
          persistingPercent={row.original.persisting_percent}
          tapestryEnabled={row.original.tapestry_enabled}
          tapestryStage={row.original.tapestry_stage}
        />
      ),
  },
  {
    id: 'uploadedBy',
    header: 'Uploaded By',
    size: 110,
    enableSorting: false,
    meta: { alignment: 'left', numeric: false },
    cell: ({ row }) =>
      row.original.__upload
        ? '—'
        : `${row.original.uploaded_by.firstName} ${row.original.uploaded_by.lastName}`,
  },
  {
    id: 'createdDate',
    accessorKey: 'createdDate',
    header: 'Uploaded',
    size: 150,
    enableSorting: true,
    meta: { alignment: 'left', numeric: false },
    cell: ({ row }) => DateTime.fromISO(row.original.createdDate).toLocal().toFormat('ff'),
  },
  {
    id: 'rows',
    header: 'Rows',
    size: 70,
    enableSorting: false,
    meta: { alignment: 'right', numeric: true },
    cell: ({ row }) =>
      row.original.processingReport.new_count != null
        ? `${row.original.processingReport.new_count} rows`
        : '',
  },
];

const getRowId = (row: TapeRow): string => row.id;

// Module-level so the selection config stays referentially stable across renders.
const canSelectRow = (row: Row<WithSubData<any>>): boolean => isArchivable(row.original as TapeRow);

type Props = {
  tableRows: TapesUploadedTableFile[] | undefined;
  inProgressFiles?: InProgressFile[];
  error?: ApolloError;
  selection: {
    selected: RowSelectionState;
    onSelectionChange: OnChangeFn<RowSelectionState>;
  };
};

const TapesUploadedTable: React.FC<Props> = ({ tableRows, inProgressFiles, error, selection }) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdDate', desc: true }]);

  // WithSubData rows need last_updated_date — it doubles as the table's memo
  // key, so it must change whenever anything the row renders changes (state
  // transitions, progress ticks, archive flips). Omitting a field here would
  // freeze its updates under the 2s poll.
  const rows: TapeRow[] = useMemo(
    () =>
      (tableRows ?? []).map((tape) => ({
        ...tape,
        // Codegen omits __typename from the fragment type; WithSubData requires it.
        __typename: 'File',
        last_updated_date: [
          tape.state,
          // Tapestry rows render their status off tapestry_stage, so it must be
          // in the render key or the pill freezes as the pipeline advances.
          tape.tapestry_stage,
          tape.processing_percent,
          tape.persisting_percent,
          tape.persisted_count,
          tape.archived_at,
          tape.processingReport.new_count,
        ].join('|'),
      })),
    [tableRows],
  );

  // Optimistic rows for in-flight uploads, rendered as first-class rows in the
  // same table (sorted to the top via their startedAt timestamp). They retire
  // atomically when the server row arrives — see buildOptimisticRow + the
  // filter in UploadHistory.
  const optimisticRows: TapeRow[] = useMemo(
    () => (inProgressFiles ?? []).map(buildOptimisticRow),
    [inProgressFiles],
  );

  const allRows = useMemo(() => [...optimisticRows, ...rows], [optimisticRows, rows]);

  const handleRowClick = useCallback(
    (row: Row<TapeRow>) => {
      // Tapestry tapes always have a meaningful detail page (live pipeline
      // status, the human gates, or the terminal/outcome panel), so they're
      // navigable regardless of the legacy FileState, which doesn't model the
      // pipeline and would otherwise leave the row un-clickable.
      if (NAVIGABLE_STATES.has(row.original.state) || row.original.tapestry_enabled === true) {
        navigate(`/portfolio/tape-upload/${row.original.id}`);
      }
    },
    [navigate],
  );

  const getRowProps = useCallback(
    (row: Row<TapeRow>) =>
      ({
        'data-testid': 'tape-upload-row',
        'data-archived': row.original.archived_at != null ? 'true' : 'false',
        className: [
          row.original.archived_at != null ? 'opacity-60' : '',
          NAVIGABLE_STATES.has(row.original.state) || row.original.tapestry_enabled === true
            ? 'cursor-pointer'
            : 'cursor-not-allowed',
        ]
          .filter(Boolean)
          .join(' '),
      }) as React.HTMLAttributes<HTMLTableRowElement>,
    [],
  );

  const tableSelection = useMemo(
    () => ({
      ...selection,
      canSelectRow,
      rowCheckboxTestId: 'tape-row-select-checkbox',
      selectAllTestId: 'tape-table-select-all-checkbox',
    }),
    [selection],
  );

  const tableSorting = useMemo(() => ({ state: sorting, onSortingChanged: setSorting }), [sorting]);

  if (error) {
    console.error('Error fetching loan uploads', error);
    return <div>Error fetching data</div>;
  } else if (!tableRows) {
    return <div>Loading...</div>;
  }

  return (
    <div className="mb-4 min-h-[200px] overflow-y-auto text-[12px] text-white">
      <DataDisplayTable
        data={allRows}
        columns={TAPE_COLUMNS}
        noDataMessage="No tapes uploaded yet"
        getRowId={getRowId}
        selection={tableSelection}
        sorting={tableSorting}
        isDraggable={false}
        canResize={false}
        isSelectionInfoIcon={false}
        onRowClick={handleRowClick}
        getRowProps={getRowProps}
        stylingOptions={{ hoverRowColor: 'gray' }}
      />
      <Tooltip id={FILENAME_TOOLTIP_ID} className="z-[100] max-w-[300px]" place="top" />
    </div>
  );
};

export type { InProgressFile };
export default TapesUploadedTable;
export { TapesUploadedTableFragments, StatusCell };
